import { Component } from '@mycolorway/tao';
import eventListenerMixin from 'next/javascripts/mixins/event-listener-mixin';
import { DataProvider, Option } from 'next/javascripts/components/select-list';

export default Component('tr-selector-field', {
  mixins: [eventListenerMixin],

  properties: {
    multiple: {
      type: Boolean,
      default: false,
    },

    remote: {
      type: Object,
      default: null,
    },

    creation: {
      type: Object,
      default: null,
    },

    lazy: {
      type: Boolean,
      default: false,
    },

    selected: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    readonly: {
      type: Boolean,
      default: false,
      observer() {
        this.nativeField.readonly = this.readonly;
        const selectEl = this.result.querySelector('.mdc-select');
        if (selectEl) selectEl.classList.toggle('mdc-select--disabled', this.readonly);
        if (this.readonly) this.toggleList(false);
      },
    },

    closeAfterSelect: {
      type: Boolean,
      default: false,
    },
  },

  ready() {
    this.selectedOptions = this.multiple ? [] : null;
    this.nativeField = this.querySelector('select');
    this.dataProvider = new DataProvider(this.nativeField, this.remote, this.creation);

    this.result = this.querySelector('.selector-field-result');
    this.list = this.querySelector('.selector-field-list');
    this.list.setAnchor(this.result);

    this.initSelectedOptions();
    if (this.creation) this.list.createable = true;
    if (!this.lazy) this.list.setOptions(this.dataProvider.options);

    this.addEventListeners();
  },

  disconnected() {
    this.stopListening();
  },

  selectOption(option) {
    if (!option || option === this.selectedOptions) return false;
    if (this.multiple) {
      const selectedIndex = this.selectedOptions
        .findIndex(selected => selected.value === option.value);
      if (selectedIndex !== -1) return false;
    }

    this.result.selectOption(option);

    if (this.multiple) {
      this.selectedOptions.push(option);
    } else {
      this.list.clearSelected();
      this.selectedOptions = option;
    }

    this.list.selectOption(option);
    this.selected = true;
    return true;
  },

  unselectOption(option) {
    if (this.multiple) {
      if (!option) return false;

      const selectedIndex = this.selectedOptions
        .findIndex(selected => selected.value === option.value);
      if (selectedIndex === -1) return false;

      this.result.unselectOption(option);
      this.list.unselectOption(option);
      this.selectedOptions.splice(selectedIndex, 1);
      this.selected = this.selectedOptions.length > 0;
    } else {
      this.clearSelected();
    }

    return true;
  },

  clearSelected() {
    this.result.clearSelected();
    this.list.clearSelected();
    this.selected = false;
    this.selectedOptions = this.multiple ? [] : null;
  },

  filterList(value, callback) {
    this.list.loading = true;
    this.dataProvider.filter(value, (options, totalItemCount) => {
      this.list.loading = false;
      this.list.filterValue = value;
      this.list.setOptions(options);
      this.list.totalItemCount = totalItemCount || 0;
      if (callback) callback();
    });
  },

  createItem(value) {
    if (!value) return;
    this.list.loading = true;
    this.dataProvider.create(value, (option, options) => {
      this.list.loading = false;
      this.list.setOptions(options);
      this.selectOption(option);
      this.notifyChange();
    });
  },

  addEventListeners() {
    if (this.lazy) {
      this.listenTo(this, 'mouseenter', () => {
        if (this.wakedUp) return;
        this.list.setOptions(this.dataProvider.options);
        this.wakedUp = true;
      });
    }

    this.listenTo(this.result, {
      'tao:unselect': (event) => {
        this.unselectOption(event.detail);
        if (this.list.isOpen()) this.toggleList(true);
        this.notifyChange();
      },
      'tao:clear': () => {
        this.clearSelected();
        this.notifyChange();
      },
      'tr-selector-field-result:activeClick': this.handleResultActive.bind(this),
    });

    this.listenTo(this.list, {
      'tao:select': (event) => {
        const option = event.detail;
        const selected = this.selectOption(option);

        if (selected && this.closeAfterSelect) {
          this.toggleList(false);
        } else if (selected) {
          this.toggleList(this.multiple);
        } else {
          this.unselectOption(option);
          this.toggleList(true);
        }

        this.notifyChange();
      },
      'tr-selector-field-list:closed': () => {
        this.list.setOptions(this.dataProvider.options);
      },
      'tr-selector-field-list:search': event => this.filterList(event.detail),
      'tr-selector-field-list:create': event => this.createItem(event.detail),
    });
  },

  handleResultActive() {
    if (!this.disabled && !this.readonly) this.toggleList(!this.list.isOpen());
  },

  toggleList(open = true) {
    if (open) {
      this.list.open();
    } else {
      this.list.close();
    }
  },

  notifyChange() {
    this.namespacedTrigger('change', {
      detail: this.getValue(),
      bubbles: true,
    });
  },

  getValue() {
    if (this.multiple) {
      return this.selectedOptions.map(option => option.value);
    }

    return this.selectedOptions ? this.selectedOptions.value : null;
  },

  setValue(value) {
    const values = Array.isArray(value) ? value : [value];
    values.forEach((v) => {
      const option = this.dataProvider.getOption(v);
      if (option) this.selectOption(option);
    });
  },

  initSelectedOptions() {
    [...this.nativeField.selectedOptions].forEach((nativeOption) => {
      if (!nativeOption.value) return;

      let { option } = nativeOption.dataset;
      if (option) {
        option = Option.fromJSON(JSON.parse(option), nativeOption.dataset.group);
      } else {
        option = Option.fromElement(nativeOption);
      }

      this.selectOption(option);
    });
  },
});
